import React from "react";
import heroImg from "../assets/security/security-hero.png";
import phonesImg from "../assets/security/security-phones.png";
import step1 from "../assets/security/security-step-1.png";
import step2 from "../assets/security/security-step-2.png";
import step3 from "../assets/security/security-step-3.png";
import step4 from "../assets/security/security-step-4.png";
import step5 from "../assets/security/security-step-5.png";
import step6 from "../assets/security/security-step-6.png";

const featureCards = [
  {
    num: "01",
    title: "Arrive & Clock In",
    description: "Photo, location, and site verification. No buddy punching.",
    image: step1,
  },
  {
    num: "02",
    title: "Complete Patrols",
    description: "Scan checkpoints, take photos, confirm completion. No skipped rounds.",
    image: step2,
  },
  {
    num: "03",
    title: "Report Incidents",
    description: "Log issues in real time. Photos, notes, location, timestamps. Instant visibility.",
    image: step3,
  },
  {
    num: "04",
    title: "Supervisor Oversight",
    description: "Monitor live status, enforce tasks, get alerted on misses.",
    image: step4,
  },
  {
    num: "05",
    title: "Command Center",
    description: "See everything across all sites. Action with data. Answer with proof.",
    image: step5,
  },
  {
    num: "06",
    title: "Emergency Response",
    description: "One tap sends alert and location. Faster response, safer teams.",
    image: step6,
  },
];

export default function SecurityPage() {
  return (
    <div style={styles.page}>
      <div style={styles.heroSection}>
        <div style={styles.leftPanel}>
          <div style={styles.pill}>NEO • SECURITY</div>

          <h1 style={styles.heroTitle}>
            <span style={styles.titleLine}>Real-Time Control.</span>
            <span style={styles.titleAccent}>Accountability.</span>
            <span style={styles.titleLine}>Proof.</span>
          </h1>

          <p style={styles.heroLead}>
            MatrixOS doesn&apos;t just track security.
            <br />
            <span style={styles.heroAccent}>It commands it.</span>
          </p>

          <p style={styles.heroBody}>
            Neo turns every guard, patrol, and incident into verified action. No guessing.
            No paperwork. No excuses.
          </p>

          <div style={styles.iconRow}>
            {[
              ["LIVE", "OVERSIGHT"],
              ["VERIFIED", "ACTIONS"],
              ["INSTANT", "ALERTS"],
              ["AUDIT", "& PROOF"],
            ].map(([a, b]) => (
              <div key={a} style={styles.iconBlock}>
                <div style={styles.iconBadge}>⌁</div>
                <div style={styles.iconLabel}>{a}</div>
                <div style={styles.iconLabel}>{b}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.visualPanel}>
          <img src={heroImg} alt="Security guard hero" style={styles.heroImage} />
          <div style={styles.dashboardWrap}>
            <img src={step5} alt="MatrixOS command center" style={styles.dashboardImage} />
          </div>
          <div style={styles.phonesWrap}>
            <img src={phonesImg} alt="Neo security app screens" style={styles.phonesImage} />
          </div>
        </div>
      </div>

      <div style={styles.divider} />

      <div style={styles.flowSection}>
        <div style={styles.flowHeader}>
          <div style={styles.flowEyebrow}>Night Shift Flow</div>
          <h2 style={styles.flowTitle}>From Clock-In to Complete Control</h2>
          <p style={styles.flowSub}>One connected system. Every action verified.</p>
        </div>

        <div style={styles.cardGrid}>
          {featureCards.map((card) => (
            <div key={card.num} style={styles.card}>
              <img src={card.image} alt={card.title} style={styles.cardImage} />
              <div style={styles.cardNum}>{card.num}</div>
              <div style={styles.cardBody}>
                <div style={styles.cardTitle}>{card.title}</div>
                <div style={styles.cardText}>{card.description}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={styles.footerLine}>
          MatrixOS for Security. <span style={styles.footerAccent}>Powered by Neo.</span>
        </div>
      </div>
    </div>
  );
}

const panelBg = "linear-gradient(180deg, rgba(6,15,29,0.92) 0%, rgba(3,10,20,0.96) 100%)";
const border = "1px solid rgba(84, 224, 255, 0.16)";

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at 50% 16%, rgba(15,126,255,0.12), transparent 26%), linear-gradient(180deg, #03101d 0%, #020a14 100%)",
    color: "#f1fbff",
    padding: "24px 24px 36px",
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  heroSection: {
    maxWidth: "1520px",
    margin: "0 auto",
    minHeight: "560px",
    display: "grid",
    gridTemplateColumns: "0.92fr 1.18fr",
    gap: "0",
    borderRadius: "26px 26px 0 0",
    overflow: "hidden",
    position: "relative",
  },
  leftPanel: {
    background: "linear-gradient(180deg, rgba(3,8,16,0.96) 0%, rgba(2,7,14,0.98) 100%)",
    padding: "30px 34px 26px",
    position: "relative",
    zIndex: 2,
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "999px",
    border: "1px solid rgba(65, 220, 255, 0.5)",
    color: "#8cefff",
    padding: "10px 18px",
    fontSize: "0.98rem",
    fontWeight: 800,
    letterSpacing: "0.12em",
    background: "rgba(0, 112, 255, 0.08)",
    boxShadow: "0 0 24px rgba(39, 214, 255, 0.22), inset 0 0 18px rgba(39, 214, 255, 0.08)",
  },
  heroTitle: {
    margin: "28px 0 0",
    display: "grid",
    gap: "2px",
    lineHeight: 0.95,
    fontWeight: 900,
    letterSpacing: "-0.05em",
    fontSize: "clamp(3.2rem, 5vw, 6rem)",
  },
  titleLine: {
    color: "#f4fbff",
  },
  titleAccent: {
    color: "#20a7ff",
  },
  heroLead: {
    margin: "24px 0 0",
    fontSize: "clamp(1.3rem, 1.75vw, 2rem)",
    lineHeight: 1.4,
    color: "#d4e8f2",
  },
  heroAccent: {
    color: "#1fa7ff",
    fontWeight: 700,
  },
  heroBody: {
    margin: "16px 0 0",
    maxWidth: "520px",
    fontSize: "1rem",
    lineHeight: 1.55,
    color: "#d6e7f1",
  },
  iconRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: "18px",
    marginTop: "36px",
    maxWidth: "560px",
  },
  iconBlock: {
    textAlign: "center",
    color: "#e8fbff",
  },
  iconBadge: {
    width: "48px",
    height: "48px",
    margin: "0 auto 10px",
    borderRadius: "14px",
    display: "grid",
    placeItems: "center",
    color: "#169eff",
    border: "1px solid rgba(54, 199, 255, 0.3)",
    boxShadow: "0 0 18px rgba(32, 167, 255, 0.14)",
    background: "rgba(5, 21, 40, 0.72)",
    fontSize: "1.2rem",
    fontWeight: 700,
  },
  iconLabel: {
    fontSize: "0.8rem",
    fontWeight: 700,
    letterSpacing: "0.03em",
    lineHeight: 1.25,
  },
  visualPanel: {
    position: "relative",
    background: "linear-gradient(180deg, rgba(3,10,20,0.88) 0%, rgba(2,7,14,0.95) 100%)",
    overflow: "hidden",
  },
  heroImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    opacity: 0.96,
  },
  dashboardWrap: {
    position: "absolute",
    top: "18px",
    right: "20px",
    width: "42%",
    maxWidth: "640px",
    borderRadius: "20px",
    overflow: "hidden",
    boxShadow: "0 0 32px rgba(15,126,255,0.14)",
  },
  dashboardImage: {
    width: "100%",
    display: "block",
    objectFit: "cover",
  },
  phonesWrap: {
    position: "absolute",
    right: "7%",
    bottom: "-2%",
    width: "54%",
    maxWidth: "760px",
  },
  phonesImage: {
    width: "100%",
    display: "block",
    objectFit: "contain",
    filter: "drop-shadow(0 12px 28px rgba(0,0,0,0.4))",
  },
  divider: {
    maxWidth: "1520px",
    margin: "0 auto",
    height: "1px",
    background: "linear-gradient(90deg, rgba(44,167,255,0.0), rgba(44,167,255,0.2), rgba(44,167,255,0.0))",
  },
  flowSection: {
    maxWidth: "1520px",
    margin: "0 auto",
    paddingTop: "18px",
  },
  flowHeader: {
    maxWidth: "430px",
    padding: "0 2px 18px",
  },
  flowEyebrow: {
    color: "#2db8ff",
    textTransform: "uppercase",
    fontWeight: 800,
    letterSpacing: "0.16em",
    fontSize: "0.88rem",
    marginBottom: "10px",
  },
  flowTitle: {
    margin: 0,
    fontSize: "clamp(2.2rem, 4vw, 3.8rem)",
    lineHeight: 1.02,
    letterSpacing: "-0.04em",
    fontWeight: 900,
  },
  flowSub: {
    margin: "12px 0 0",
    color: "#d0e1ea",
    fontSize: "1rem",
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    gap: "14px",
    alignItems: "stretch",
  },
  card: {
    borderRadius: "18px",
    border,
    overflow: "hidden",
    background: panelBg,
    boxShadow: "0 0 0 1px rgba(255,255,255,0.02), 0 0 24px rgba(8, 20, 40, 0.28)",
  },
  cardImage: {
    width: "100%",
    aspectRatio: "1.25 / 1",
    objectFit: "cover",
    display: "block",
  },
  cardNum: {
    margin: "10px 12px 0",
    width: "32px",
    height: "24px",
    borderRadius: "8px",
    background: "#005dff",
    display: "grid",
    placeItems: "center",
    fontSize: "0.8rem",
    fontWeight: 800,
    color: "#fff",
  },
  cardBody: {
    padding: "10px 14px 16px",
  },
  cardTitle: {
    fontSize: "1.05rem",
    lineHeight: 1.15,
    fontWeight: 800,
    textTransform: "uppercase",
    marginBottom: "8px",
  },
  cardText: {
    fontSize: "0.95rem",
    lineHeight: 1.45,
    color: "#d5e6ef",
  },
  footerLine: {
    textAlign: "center",
    marginTop: "14px",
    fontSize: "1rem",
    fontWeight: 700,
    color: "#ecf8ff",
  },
  footerAccent: {
    color: "#17a8ff",
  },
};
